import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  ShoppingCart,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Card from "src/components/common/card/Card";
import Popover from "src/components/ui/Popover";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as purchaseActions from "src/redux/slices/purchaseSlice";
import * as supplierActions from "src/redux/slices/supplierSlice";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter } from "src/utils/common";
import { Badge } from "src/components/ui/Badge";
import usePlanUsage from "src/hooks/usePlanUsage";

const ORDER_STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Received", value: "received" },
  { label: "Cancelled", value: "cancelled" },
];

const orderStatusColor = (status: string) =>
  status === "received" ? "green" : status === "cancelled" ? "red" : "yellow";

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const PurchaseListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [orderStatus, setOrderStatus] = useState<
    "" | "pending" | "received" | "cancelled"
  >("");

  const { list } = useAppSelector((state) => state.purchase);
  const { supplierList } = useAppSelector((state) => state.supplier);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreatePurchase =
    isTenantAdmin || (permissions?.purchases || []).includes("create");
  const canEditPurchase =
    isTenantAdmin || (permissions?.purchases || []).includes("edit");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { planUsage } = usePlanUsage();
  const isRowOwner = (row: any) => {
    const createdById =
      typeof row?.createdBy === "string" ? row.createdBy : row?.createdBy?._id;
    if (!createdById || !user?._id) return false;
    return String(createdById) === String(user._id);
  };

  const purchaseLimit = planUsage?.plan?.features?.totalPurchaseOrders;
  const purchaseUsage = planUsage?.usage?.totalPurchaseOrders;
  const isPlanExpired = planUsage?.planStatus === "expired";
  const isPurchaseLimitReached =
    typeof purchaseLimit === "number" &&
    typeof purchaseUsage === "number" &&
    purchaseUsage >= purchaseLimit;
  const disableCreatePurchase = isPlanExpired || isPurchaseLimitReached;

  const getPurchases = useCallback(
    (page: number) => {
      dispatch(
        purchaseActions.getPurchaseListRequest({
          page,
          limit,
          ...(search ? { search } : {}),
          ...(supplierId ? { supplierId } : {}),
          ...(orderStatus ? { orderStatus } : {}),
        })
      );
    },
    [dispatch, limit, search, supplierId, orderStatus]
  );

  useEffect(() => {
    dispatch(supplierActions.getSupplierListRequest({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Debounce the search box before it hits the API/filters
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getPurchases(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, supplierId, orderStatus, limit]);

  const handlePageChange = (page: number) => getPurchases(page);

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setSupplierId("");
    setOrderStatus("");
  };
  const hasActiveFilters = !!(search || supplierId || orderStatus);

  return (
    <DashboardLayout
      meta={{
        title: "Purchase Orders | Invyntra",
        description: "Track and manage all product purchase orders.",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <ShoppingCart size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Purchases
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Purchase orders and supplier management.
              </p>
            </div>
          </div>

          {canCreatePurchase && (
            <Button
              rounded="full"
              onClick={() => navigate("/app/purchase/create")}
              disabled={disableCreatePurchase}
              title={
                isPlanExpired
                  ? "Plan expired. Please upgrade."
                  : isPurchaseLimitReached
                  ? "Order limit reached. Please upgrade."
                  : undefined
              }
            >
              <Plus size={16} className="mr-1" />
              <span>
                {disableCreatePurchase
                  ? "Limit Reached"
                  : "Create purchase order"}
              </span>
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search order id..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Supplier"
                value={supplierId}
                onChange={setSupplierId}
                allLabel="All suppliers"
                options={(supplierList?.data?.data || []).map((s: any) => ({
                  label: s.name,
                  value: s._id,
                }))}
              />

              <FilterSelect
                variant="boxed"
                label="Order Status"
                value={orderStatus}
                onChange={(val) => setOrderStatus(val as typeof orderStatus)}
                allLabel="All statuses"
                options={ORDER_STATUS_OPTIONS}
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Purchase orders"
            description="Filter by order date, status, or supplier."
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "purchaseOrderId",
                header: "PO No",
                accessor: ({ row }) => (
                  <Link
                    to={`/app/purchase/${row._id}`}
                    className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                  >
                    #{row.purchaseOrderId}
                  </Link>
                ),
              },
              {
                key: "orderDate",
                header: "Order Date",
                accessor: ({ row }) =>
                  formatDateTime(row.orderDate || row.createdAt, "DD MMM YYYY"),
              },
              {
                key: "supplierId.name",
                header: "Supplier",
                accessor: ({ row }) => row.supplierId?.name || "-",
              },
              {
                key: "grandTotal",
                header: "Total",
                align: "right",
                accessor: ({ row }) => (
                  <span className="font-semibold text-primary-700 dark:text-primary-400">
                    {currencyFormatter(row.grandTotal)}
                  </span>
                ),
              },
              {
                key: "orderStatus",
                header: "Status",
                accessor: ({ row, key }) =>
                  row[key] ? (
                    <Badge
                      color={orderStatusColor(row[key])}
                      text={capitalize(row[key])}
                    />
                  ) : (
                    "-"
                  ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) => (
                  <Popover
                    trigger={
                      <button
                        className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                        title="Actions"
                      >
                        <MoreVertical size={16} />
                      </button>
                    }
                  >
                    {(close) => (
                      <div className="w-44 py-1.5">
                        <button
                          className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          onClick={() => {
                            close();
                            navigate(`/app/purchase/${row._id}`);
                          }}
                        >
                          <Eye size={14} />
                          View
                        </button>
                        {canEditPurchase &&
                          row.orderStatus === "pending" &&
                          (isTenantAdmin || isRowOwner(row)) && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                navigate(`/app/purchase/${row._id}/edit`);
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                          )}
                      </div>
                    )}
                  </Popover>
                ),
              },
            ]}
            data={list?.data?.data}
            pagination={list?.data?.pagination}
            onPageChange={handlePageChange}
            loading={list.loading}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PurchaseListPage;
